import React, { useEffect, useRef, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Cookies from "js-cookie";

// Components
import Navbar from "./components/Navbar";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import DesktopOnlyRoute from "./components/DesktopOnlyRoute";

// Public Pages
import Home from "./pages/Home";
import Bleeps from "./pages/Bleeps";
import ReportPage from "./pages/ReportPage";
import Search from "./pages/Search";
import Store from "./pages/Store";
import Post from "./pages/Post";
import SignIn from "./pages/SignIn";
import Downloads from "./pages/Downloads";
import Verify from "./pages/Verify";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import About from "./pages/About";
import Contact from "./pages/Contact";
import CBQs from "./pages/CBQs";

// The legacy stylesheet, and the only thing keeping it in the bundle. It still
// owns the dark palette every feed card, the navbar chrome and the console
// dropdowns are painted with, but nothing imports it by name any more: the
// theme-layer migration removed the import from each page it touched, and the
// last two holdouts were MyProfile and MyBleeps, which /me replaced. Deleting
// them dropped App.css out of the build entirely.
//
// The position is load-bearing, not tidy-uppable. It has to come after
// index.js's bleep-theme.css (the theme is written to beat it on specificity,
// which assumes it is the earlier of the two) and after the public page imports
// above, which is exactly where MyProfile used to pull it in. Moving it above
// them would put it ahead of components/css/bleep-form.css and flip every
// equal-specificity `.bleep-*` rule the two files share.
import "./App.css";

// Private Pages
import MobileBlock from "./pages/MobileBlock";
import Me from "./pages/Me";
import AccountSettings from "./pages/AccountSettings";
import Dashboard from "./pages/Dashboard";
import BleepMap from "./pages/BleepMap";
import Accounts from "./pages/Accounts";
import Clients from "./pages/Clients";
import Analytics from "./pages/Analytics";
import Ads from "./pages/Ads";
import BuyGenuine from "./pages/BuyGenuine";
import Articles from "./pages/Articles";
import ArticleEditor from "./pages/ArticleEditor";
import ArticleView from "./pages/ArticleView";
import BleSTOrders from "./pages/BleSTOrders";
import IrisInvoice from "./pages/IrisInvoice";
import BleSTInvoice from "./pages/BleSTInvoice";
import BlestStatementOfAccounts from "./pages/BlestStatementOfAccounts";
import IrisStatementOfAccounts from "./pages/IrisStatementOfAccounts";
import MyBleST from "./pages/MyBleST";
import BleSTMap from "./pages/BleSTMap";
import ProductSKUs from "./pages/ProductSKUs";

const App = () => {
  const hostname = window.location.hostname;
  const domain = hostname.endsWith(".ke") ? "ke" : "ug";

  const [userData, setUserData] = useState(() => [
    Cookies.get("userId"),
    Cookies.get("userType"),
    Cookies.get("userName"),
    Cookies.get("userEmail") || Cookies.get("userPhone"),
    Cookies.get("userPassword"),
    Cookies.get("userOrg"),
    Cookies.get("userPermissions"),
    Cookies.get("userAnonymous"),
  ]);
  const [searchTerm, setSearchTerm] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const lastHeartbeatRef = useRef(0);

  const getUserData = () => {
    const newData = [
      Cookies.get("userId"),
      Cookies.get("userType"),
      Cookies.get("userName"),
      Cookies.get("userEmail") || Cookies.get("userPhone"),
      Cookies.get("userPassword"),
      Cookies.get("userOrg"),
      Cookies.get("userPermissions"),
      Cookies.get("userAnonymous"),
    ];
    setUserData(newData);
  };

  const signOut = async () => {
    const userId = Cookies.get("userId");
    const deviceId = localStorage.getItem("bleepWebDeviceId");
    if (userId && deviceId) {
      try {
        await fetch(`/api/users/${userId}/devices/signout`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ deviceId }),
        });
      } catch (_) {}
    }
    Cookies.remove("userId");
    Cookies.remove("userType");
    Cookies.remove("userName");
    Cookies.remove("userEmail");
    Cookies.remove("userPhone");
    Cookies.remove("userPassword");
    Cookies.remove("userOrg");
    Cookies.remove("userPermissions");
    // Deliberately a document load, unlike every other navigation in the app.
    // Clearing the cookies does not clear what is already mounted: the navbar's
    // display name and avatar, the sidenav's fetched permissions, each page's
    // own copy of userId, and any request already in flight for the old
    // account. navigate() would leave all of that on screen. This is the only
    // guarantee that nothing of the previous session survives, it happens once,
    // and the pre-paint background in public/index.html means it no longer
    // flashes white on the way out.
    window.location.href = "/";
  };

  useEffect(() => {
    getUserData();

    const checkUserValidity = () => {
      const userId = Cookies.get("userId");
      const path = window.location.pathname;

      const publicPaths = [
        "/",
        "/bleeps",
        // Public on purpose. The report form was reachable signed out when it
        // was a modal, and its submit button turns into a sign-in link; leaving
        // it out of this list would bounce those visitors to /signin the moment
        // the validity check ran.
        "/report",
        "/search",
        "/store",
        "/signin",
        "/downloads",
        "/verify",
        "/termsofservice",
        "/privacypolicy",
        "/about",
        "/contact",
        "/cbqs",
      ];

      const isPostPath = path.startsWith("/post/");
      const isArticlePath = path.startsWith("/article/");
      const isPublic =
        publicPaths.includes(path) || isPostPath || isArticlePath;

      if (!userId && !isPublic) {
        // navigate, not window.location: a document load here repaints from the
        // browser's blank canvas. /signin is in publicPaths, so this cannot
        // bounce again once it lands.
        navigate("/signin");
      }
    };

    checkUserValidity();

    const interval = setInterval(() => {
      checkUserValidity();
    }, 60000);

    return () => clearInterval(interval);
    // navigate is referentially stable in react-router v6, so naming it here
    // does not restart the interval.
  }, [navigate]);

  useEffect(() => {
    if (location.pathname !== "/search") {
      setSearchTerm("");
    } else {
      const urlParams = new URLSearchParams(window.location.search);
      const term = urlParams.get("q");
      if (term) {
        setSearchTerm(term);
      }
    }

    const userId = Cookies.get("userId");
    const deviceId = localStorage.getItem("bleepWebDeviceId");
    const now = Date.now();
    if (userId && deviceId && now - lastHeartbeatRef.current > 60_000) {
      lastHeartbeatRef.current = now;
      fetch(`/api/users/${userId}/devices/heartbeat`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceId, os: "web" }),
      }).catch(() => {});
    }
  }, [location.pathname]);

  const refreshUserData = () => {
    getUserData();
  };

  const hideNavbarPaths = ["/signin", "/mobileblock"];

  return (
    <>
      <ScrollToTop />
      {!hideNavbarPaths.includes(location.pathname) && (
        <Navbar
          domain={domain}
          userData={userData}
          signOut={signOut}
          search={searchTerm}
        />
      )}
      <Routes>
        {/* Public Routes */}
        <Route
          path="/"
          element={
            userData[0] && userData[1] && userData[1] !== "user" ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Home domain={domain} userData={userData} />
            )
          }
        />
        <Route
          path="/bleeps"
          element={
            userData[0] && userData[1] && userData[1] !== "user" ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Bleeps domain={domain} userData={userData} />
            )
          }
        />
        {/* Public, like the modal was: a signed-out visitor can fill the form
            in and the submit becomes a sign-in link. Guarded the same way as
            the feed routes, since staff accounts do not file reports. */}
        <Route
          path="/report"
          element={
            userData[0] && userData[1] && userData[1] !== "user" ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <ReportPage domain={domain} />
            )
          }
        />
        <Route
          path="/search"
          element={
            userData[0] && userData[1] && userData[1] !== "user" ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Search domain={domain} userData={userData} />
            )
          }
        />
        <Route
          path="/store"
          element={<Store domain={domain} userData={userData} />}
        />
        <Route
          path="/post/:id"
          element={
            <Post
              domain={domain}
              userData={userData}
              refreshUserData={refreshUserData}
            />
          }
        />
        <Route
          path="/signin"
          element={
            <SignIn
              domain={domain}
              userData={userData}
              handleSignIn={getUserData}
            />
          }
        />
        <Route
          path="/downloads"
          element={<Downloads domain={domain} userData={userData} />}
        />
        <Route
          path="/verify"
          element={<Verify domain={domain} userData={userData} />}
        />
        <Route
          path="/termsofservice"
          element={<TermsOfService domain={domain} userData={userData} />}
        />
        <Route
          path="/privacypolicy"
          element={<PrivacyPolicy domain={domain} userData={userData} />}
        />
        <Route
          path="/about"
          element={<About domain={domain} userData={userData} />}
        />
        <Route
          path="/contact"
          element={<Contact domain={domain} userData={userData} />}
        />
        <Route
          path="/cbqs"
          element={<CBQs domain={domain} userData={userData} />}
        />

        {/* Mobile block */}
        <Route path="/mobileblock" element={<MobileBlock />} />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DesktopOnlyRoute>
                <Dashboard domain={domain} userData={userData} />
              </DesktopOnlyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bleepmap"
          element={
            <ProtectedRoute>
              <DesktopOnlyRoute>
                <BleepMap domain={domain} userData={userData} />
              </DesktopOnlyRoute>
            </ProtectedRoute>
          }
        />
        {/* The account and the reporter's own feed, on one page. Staff accounts
            get the account half only; they cannot post. */}
        <Route
          path="/me"
          element={
            <ProtectedRoute>
              <Me
                domain={domain}
                userData={userData}
                refreshUserData={refreshUserData}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/me/settings"
          element={
            <ProtectedRoute>
              <AccountSettings
                domain={domain}
                userData={userData}
                refreshUserData={refreshUserData}
              />
            </ProtectedRoute>
          }
        />
        {/* The two pages this split replaces. Kept as redirects: both were
            linked from the navbar and the homepage for long enough to be
            bookmarked. Each points at the half that carries its old content -
            /myprofile was the three change-a-detail cards, which are now on
            /me/settings rather than on /me itself. */}
        <Route
          path="/mybleeps"
          element={<Navigate to="/me#bleeps" replace />}
        />
        <Route
          path="/myprofile"
          element={<Navigate to="/me/settings" replace />}
        />
        <Route
          path="/clients"
          element={
            <ProtectedRoute>
              <DesktopOnlyRoute>
                <Clients domain={domain} userData={userData} />
              </DesktopOnlyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts"
          element={
            <ProtectedRoute>
              <DesktopOnlyRoute>
                <Accounts domain={domain} userData={userData} />
              </DesktopOnlyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <DesktopOnlyRoute>
                <Analytics domain={domain} userData={userData} />
              </DesktopOnlyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ads"
          element={
            <ProtectedRoute>
              <DesktopOnlyRoute>
                <Ads domain={domain} userData={userData} />
              </DesktopOnlyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/buygenuine"
          element={
            <ProtectedRoute>
              <DesktopOnlyRoute>
                <BuyGenuine domain={domain} userData={userData} />
              </DesktopOnlyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/articles"
          element={
            <ProtectedRoute allowedTypes={["superadmin", "backoffice"]}>
              <DesktopOnlyRoute>
                <Articles domain={domain} userData={userData} />
              </DesktopOnlyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/articles/new"
          element={
            <ProtectedRoute allowedTypes={["superadmin", "backoffice"]}>
              <DesktopOnlyRoute>
                <ArticleEditor domain={domain} userData={userData} />
              </DesktopOnlyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/articles/:id/edit"
          element={
            <ProtectedRoute allowedTypes={["superadmin", "backoffice"]}>
              <DesktopOnlyRoute>
                <ArticleEditor domain={domain} userData={userData} />
              </DesktopOnlyRoute>
            </ProtectedRoute>
          }
        />
        <Route path="/article/:id" element={<ArticleView />} />
        <Route
          path="/blestorders"
          element={
            <ProtectedRoute>
              <DesktopOnlyRoute>
                <BleSTOrders domain={domain} userData={userData} />
              </DesktopOnlyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/irisinvoice"
          element={
            <ProtectedRoute>
              <DesktopOnlyRoute>
                <IrisInvoice domain={domain} userData={userData} />
              </DesktopOnlyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/blestinvoice"
          element={
            <ProtectedRoute>
              <DesktopOnlyRoute>
                <BleSTInvoice domain={domain} userData={userData} />
              </DesktopOnlyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bleststatementofaccounts"
          element={
            <ProtectedRoute>
              <DesktopOnlyRoute>
                <BlestStatementOfAccounts domain={domain} userData={userData} />
              </DesktopOnlyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/irisstatementofaccounts"
          element={
            <ProtectedRoute>
              <DesktopOnlyRoute>
                <IrisStatementOfAccounts domain={domain} userData={userData} />
              </DesktopOnlyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/myblest"
          element={
            <ProtectedRoute>
              <DesktopOnlyRoute>
                <MyBleST domain={domain} userData={userData} />
              </DesktopOnlyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/blestmap"
          element={
            <ProtectedRoute>
              <DesktopOnlyRoute>
                <BleSTMap domain={domain} userData={userData} />
              </DesktopOnlyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/productskus"
          element={
            <ProtectedRoute>
              <DesktopOnlyRoute>
                <ProductSKUs domain={domain} userData={userData} />
              </DesktopOnlyRoute>
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
};

export default App;
